import React, { useState, useCallback, useEffect } from "react";
import { Box, Alert } from "@mui/material";
import { useChatbotMutations } from "../../hooks/useChatbot";
import { ChatbotFlowEditor } from "./index";
import ChatbotTreeView from "./ChatbotTreeView";
import ChatbotNodePanel from "./ChatbotNodePanel";
import ChatbotSimulator from "./ChatbotSimulator";
import { useTranslation } from "react-i18next";
import {
  hasLocalExceptionForParent,
  canDeleteSlot,
} from "./chatbotSlotParentUtils";
import { useChatbotSlots } from "../../hooks/useChatbot";

/**
 * Área do editor de fluxo (canvas/árvore + simulador), sem cabeçalho de rota.
 */
export default function ChatbotFlowWorkspace({
  userId,
  viewMode = "canvas",
  error,
  setError,
  focusSlotRequest,
  onNavigateToSlot,
}) {
  const { t } = useTranslation("settings");
  const { createSlot, updateSlot, deleteSlot } = useChatbotMutations(userId);
  const { data: slots = [] } = useChatbotSlots(userId);
  const [selectedSlotTree, setSelectedSlotTree] = useState(null);
  const [isTreePanelOpen, setIsTreePanelOpen] = useState(false);
  const [revealSlotId, setRevealSlotId] = useState(null);
  const [simulatorActiveSlotId, setSimulatorActiveSlotId] = useState(null);

  const handleSimulatorActiveSlotChange = useCallback((slotId) => {
    const next = slotId ? Number(slotId) : null;
    setSimulatorActiveSlotId(next);
    if (next) setRevealSlotId(next);
  }, []);

  useEffect(() => {
    const slotId = focusSlotRequest?.slotId;
    if (!slotId || !slots.length) return;

    const slot = slots.find((s) => Number(s.id) === Number(slotId));
    if (!slot) return;

    setRevealSlotId(Number(slotId));

    if (viewMode === "tree") {
      setSelectedSlotTree(slot);
      setIsTreePanelOpen(true);
    }
  }, [focusSlotRequest?.ts, slots, viewMode]);

  const handleEmptyInit = useCallback(() => {
    if (!userId) return;
    setError(null);
    createSlot.mutate(
      { user_id: userId, parent_id: 0, identificador: "Início", criar_excecao: true },
      {
        onSuccess: () => setError(null),
        onError: (err) => setError(err?.message || t("chatbotConfigPage.errorInit")),
      }
    );
  }, [userId, createSlot, setError, t]);

  const handleTreeSaveSlot = (payload) => {
    if (!payload?.id) return;
    updateSlot.mutate(payload, {
      onSuccess: (updated) => {
        if (updated?.id) {
          setSelectedSlotTree(updated);
        }
        setError(null);
        setIsTreePanelOpen(false);
      },
      onError: (err) => setError(err?.message || t("chatbotConfigPage.errorSaveSlot")),
    });
  };

  const handleTreeDeleteSlot = (id) => {
    if (!id) return;
    deleteSlot.mutate(id, {
      onSuccess: () => {
        setError(null);
        setIsTreePanelOpen(false);
        setSelectedSlotTree(null);
      },
      onError: (err) => setError(err?.message || t("chatbotConfigPage.errorDeleteSlot")),
    });
  };

  const handleTreeAddChild = (parentSlot) => {
    if (!parentSlot || !userId) return;
    createSlot.mutate(
      {
        user_id: userId,
        parent_id: parentSlot.id,
        identificador: "",
        criar_excecao: false,
        global: 0,
        ignorarglobal: 0,
      },
      {
        onSuccess: (created) => {
          setError(null);
          if (created?.id != null) {
            setSelectedSlotTree(created);
            setIsTreePanelOpen(true);
          }
        },
        onError: (err) => setError(err?.message || t("chatbotConfigPage.errorCreateSubstep")),
      }
    );
  };

  const handleTreeAddException = (parentSlot) => {
    if (!parentSlot || !userId) return;
    createSlot.mutate(
      {
        user_id: userId,
        parent_id: parentSlot.id,
        identificador: "Exceção",
        nome: "",
        resposta: "",
        criar_excecao: false,
        global: 0,
        ignorarglobal: 0,
      },
      {
        onSuccess: (created) => {
          setError(null);
          if (created?.id != null) {
            setSelectedSlotTree(created);
            setIsTreePanelOpen(true);
          }
        },
        onError: (err) => setError(err?.message || t("chatbotConfigPage.errorCreateSubstep")),
      }
    );
  };

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderRadius: 1,
        px: 2,
        pb: 2,
        pt: 1.5,
        boxShadow: (th) => th.shadows[1],
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {viewMode === "canvas" ? (
          <ChatbotFlowEditor
            userId={userId}
            onEmptyInit={handleEmptyInit}
            focusSlotRequest={focusSlotRequest}
            simulatorActiveSlotId={simulatorActiveSlotId}
            onNavigateToSlot={onNavigateToSlot}
          />
        ) : (
          <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
            <Box sx={{ flex: 1.2, minWidth: 260, pr: 1.5, minHeight: 0, display: "flex", flexDirection: "column" }}>
              <ChatbotTreeView
                userId={userId}
                selectedSlotId={selectedSlotTree?.id ?? null}
                simulatorActiveSlotId={simulatorActiveSlotId}
                revealSlotId={revealSlotId}
                onSelectSlot={(slot) => {
                  setSelectedSlotTree(slot);
                  setIsTreePanelOpen(true);
                }}
                onAddChild={handleTreeAddChild}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 260 }}>
              <ChatbotNodePanel
                slot={isTreePanelOpen ? selectedSlotTree : null}
                onClose={() => setIsTreePanelOpen(false)}
                onSave={handleTreeSaveSlot}
                onDelete={handleTreeDeleteSlot}
                onAddSubpasso={
                  selectedSlotTree && selectedSlotTree.identificador !== "Exceção"
                    ? () => handleTreeAddChild(selectedSlotTree)
                    : undefined
                }
                onAddException={
                  selectedSlotTree &&
                  selectedSlotTree.identificador !== "Exceção" &&
                  !hasLocalExceptionForParent(slots, selectedSlotTree.id)
                    ? () => handleTreeAddException(selectedSlotTree)
                    : undefined
                }
                isSaving={updateSlot.isLoading}
                isDeleting={deleteSlot.isLoading}
                isAddingSubpasso={createSlot.isLoading}
                saveError={null}
                canDelete={!!selectedSlotTree && canDeleteSlot(selectedSlotTree, slots)}
                onNavigateToSlot={onNavigateToSlot}
              />
            </Box>
          </Box>
        )}
      </Box>
      <ChatbotSimulator
        userId={userId}
        onNavigateToSlot={onNavigateToSlot}
        onActiveSlotChange={handleSimulatorActiveSlotChange}
      />
    </Box>
  );
}
